import {
  ACCOUNT_NAVIGATION_BLOCK_DEFAULT_ICON,
  ACCOUNT_NAVIGATION_BLOCK_DESCRIPTION_MAX_LENGTH,
  ACCOUNT_NAVIGATION_BLOCK_NAME_MAX_LENGTH,
  AccountNavigationBlock,
} from './account-navigation-block.model';

/**
 * A block is stored as one JSON string inside a multi value custom field of the user profile, so the
 * whole definition of a card travels in a single ICM field without an own resource.
 *
 * Everything that comes back from the server is treated as untrusted input: the value may have been
 * written by an older version of this application or edited in the ICM back office. An entry that
 * cannot be read or that carries a link this application would not render is dropped instead of
 * reaching a template.
 */
export class AccountNavigationBlockMapper {
  /** returns the block of a stored custom field value, or nothing if it cannot be used */
  static fromJson(value: unknown): AccountNavigationBlock {
    const parsed = parse(value);

    if (!isUsable(parsed)) {
      return;
    }

    const { id, groupId, name, url } = parsed;

    return {
      id,
      groupId,
      name: name.slice(0, ACCOUNT_NAVIGATION_BLOCK_NAME_MAX_LENGTH),
      description: isNonEmptyString(parsed.description)
        ? parsed.description.slice(0, ACCOUNT_NAVIGATION_BLOCK_DESCRIPTION_MAX_LENGTH)
        : undefined,
      icon: AccountNavigationBlockMapper.isIconName(parsed.icon) ? parsed.icon : ACCOUNT_NAVIGATION_BLOCK_DEFAULT_ICON,
      url,
      external: AccountNavigationBlockMapper.isExternalUrl(url),
    };
  }

  /** returns the custom field value of a block */
  static toJson(block: AccountNavigationBlock): string {
    return JSON.stringify({
      id: block.id,
      groupId: block.groupId,
      name: block.name,
      // an absent description is left out instead of stored as null, it keeps the field short
      ...(block.description ? { description: block.description } : {}),
      icon: block.icon,
      url: block.url,
      external: block.external,
    });
  }

  /**
   * A bootstrap icon class. Any icon of the set is accepted, not only the samples of the picker, so a
   * user who knows the name of an icon can use it. The shape is checked because the value ends up in a
   * class attribute.
   */
  static isIconName(icon: unknown): icon is string {
    return typeof icon === 'string' && /^bi-[a-z0-9-]+$/.test(icon);
  }

  /**
   * Only an internal route and an absolute http(s) url are rendered. Everything else, above all the
   * `javascript:` and `data:` schemes, is rejected so a stored link cannot execute anything.
   */
  static isSupportedUrl(url: string): boolean {
    return AccountNavigationBlockMapper.isInternalUrl(url) || AccountNavigationBlockMapper.isExternalUrl(url);
  }

  /** an internal route, a single leading slash. `//host` is a protocol relative external url, not a route */
  static isInternalUrl(url: string): boolean {
    return /^\/(?!\/)/.test(url ?? '');
  }

  /** an absolute url of a scheme that is safe to put into an href */
  static isExternalUrl(url: string): boolean {
    return /^https?:\/\/\S+$/i.test(url ?? '');
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && !!value.trim();
}

/** the stored value as an object, or nothing if it is no JSON object at all */
function parse(value: unknown): Partial<AccountNavigationBlock> {
  if (typeof value !== 'string') {
    return;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return;
  }
}

/** true if the stored value carries everything a card needs and a link that is rendered */
function isUsable(
  parsed: Partial<AccountNavigationBlock>
): parsed is AccountNavigationBlock & { name: string; url: string } {
  return (
    !!parsed &&
    isNonEmptyString(parsed.id) &&
    isNonEmptyString(parsed.groupId) &&
    isNonEmptyString(parsed.name) &&
    isNonEmptyString(parsed.url) &&
    AccountNavigationBlockMapper.isSupportedUrl(parsed.url)
  );
}
